import { createBrowserRouter } from "react-router-dom";
import { AdminAuthPage } from "../admin/auth/AdminAuthPage";
import { AdminWorkspacePage } from "../admin/dashboard/AdminWorkspacePage";
import { AdminShell } from "../components/layout/AdminShell";
import { StorefrontShell } from "../components/layout/StorefrontShell";
import { marketingPages } from "../data/content";
import { AccountPage } from "../storefront/account/AccountPage";
import { AskPage } from "../storefront/ask/AskPage";
import { AuthPage } from "../storefront/auth/AuthPage";
import { MarketingPage } from "../storefront/blog/MarketingPage";
import { CartPage } from "../storefront/cart/CartPage";
import { CategoryPage } from "../storefront/category/CategoryPage";
import { CheckoutPage } from "../storefront/checkout/CheckoutPage";
import { OrderSuccessPage } from "../storefront/checkout/OrderSuccessPage";
import { HomePage } from "../storefront/home/HomePage";
import { PolicyPage } from "../storefront/policies/PolicyPage";
import { ProductPage } from "../storefront/product/ProductPage";
import { SearchPage } from "../storefront/search/SearchPage";
import { AdminGuard } from "./guards/AdminGuard";
import { NotFound } from "./NotFound";

const marketPage = (id: keyof typeof marketingPages) => <MarketingPage page={marketingPages[id]} />;

export const router = createBrowserRouter([
  {
    path: "/",
    element: <StorefrontShell />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "search", element: <SearchPage /> },
      { path: "category/:slug", element: <CategoryPage /> },
      { path: "product/:slug", element: <ProductPage /> },
      { path: "cart", element: <CartPage /> },
      { path: "checkout", element: <CheckoutPage /> },
      { path: "order-success/:orderId", element: <OrderSuccessPage /> },
      { path: "login", element: <AuthPage mode="login" /> },
      { path: "register", element: <AuthPage mode="register" /> },
      { path: "forgot-password", element: <AuthPage mode="forgot" /> },
      { path: "verify-otp", element: <AuthPage mode="verify" /> },
      { path: "reset-password", element: <AuthPage mode="reset" /> },
      { path: "account", element: <AccountPage view="dashboard" /> },
      { path: "account/orders", element: <AccountPage view="orders" /> },
      { path: "account/orders/:id", element: <AccountPage view="order-detail" /> },
      { path: "account/addresses", element: <AccountPage view="addresses" /> },
      { path: "account/wishlist", element: <AccountPage view="wishlist" /> },
      { path: "account/warranty", element: <AccountPage view="warranty" /> },
      { path: "account/ask", element: <AccountPage view="ask" /> },
      { path: "account/notifications", element: <AccountPage view="notifications" /> },
      { path: "account/profile", element: <AccountPage view="profile" /> },
      { path: "account/security", element: <AccountPage view="security" /> },
      { path: "ask", element: <AskPage /> },
      { path: "b2b", element: marketPage("b2b") },
      { path: "brands", element: marketPage("brands") },
      { path: "blog", element: marketPage("blog") },
      { path: "blog/:slug", element: marketPage("blog") },
      { path: "contact", element: marketPage("contact") },
      { path: "faq", element: marketPage("faq") },
      { path: "track-order", element: marketPage("track-order") },
      { path: "about", element: marketPage("about") },
      { path: "compare", element: <SearchPage /> },
      { path: "privacy-policy", element: <PolicyPage policyId="privacy-policy" /> },
      { path: "terms", element: <PolicyPage policyId="terms" /> },
      { path: "shipping-policy", element: <PolicyPage policyId="shipping-policy" /> },
      { path: "return-policy", element: <PolicyPage policyId="return-policy" /> },
      { path: "warranty-policy", element: <PolicyPage policyId="warranty-policy" /> }
    ]
  },
  { path: "/admin/login", element: <AdminAuthPage mode="login" /> },
  { path: "/admin/2fa", element: <AdminAuthPage mode="2fa" /> },
  {
    path: "/admin",
    element: (
      <AdminGuard>
        <AdminShell />
      </AdminGuard>
    ),
    children: [
      { index: true, element: <AdminWorkspacePage moduleId="dashboard" /> },
      { path: "orders", element: <AdminWorkspacePage moduleId="orders" /> },
      { path: "products", element: <AdminWorkspacePage moduleId="products" /> },
      { path: "inventory", element: <AdminWorkspacePage moduleId="inventory" /> },
      { path: "purchasing", element: <AdminWorkspacePage moduleId="purchasing" /> },
      { path: "suppliers", element: <AdminWorkspacePage moduleId="suppliers" /> },
      { path: "customers", element: <AdminWorkspacePage moduleId="customers" /> },
      { path: "b2b", element: <AdminWorkspacePage moduleId="b2b" /> },
      { path: "marketplaces", element: <AdminWorkspacePage moduleId="marketplaces" /> },
      { path: "chat", element: <AdminWorkspacePage moduleId="chat" /> },
      { path: "finance", element: <AdminWorkspacePage moduleId="finance" /> },
      { path: "reconciliation", element: <AdminWorkspacePage moduleId="reconciliation" /> },
      { path: "reports", element: <AdminWorkspacePage moduleId="reports" /> },
      { path: "returns", element: <AdminWorkspacePage moduleId="returns" /> },
      { path: "members", element: <AdminWorkspacePage moduleId="members" /> },
      { path: "roles", element: <AdminWorkspacePage moduleId="roles" /> },
      { path: "audit", element: <AdminWorkspacePage moduleId="audit" /> },
      { path: "settings", element: <AdminWorkspacePage moduleId="settings" /> },
      { path: "integrations", element: <AdminWorkspacePage moduleId="integrations" /> },
      { path: "security", element: <AdminWorkspacePage moduleId="security" /> },
      { path: "profile", element: <AdminWorkspacePage moduleId="profile" /> },
      { path: "notifications", element: <AdminWorkspacePage moduleId="notifications" /> },
      { path: "branches", element: <AdminWorkspacePage moduleId="branches" /> },
      { path: "warehouses", element: <AdminWorkspacePage moduleId="warehouses" /> },
      { path: "stock-movements", element: <AdminWorkspacePage moduleId="stock-movements" /> },
      { path: "stock-transfer", element: <AdminWorkspacePage moduleId="stock-transfer" /> },
      { path: "pricing", element: <AdminWorkspacePage moduleId="pricing" /> },
      { path: "webhooks", element: <AdminWorkspacePage moduleId="webhooks" /> },
      { path: "sync-errors", element: <AdminWorkspacePage moduleId="sync-errors" /> },
      { path: "orders/:id", element: <AdminWorkspacePage moduleId="order-detail" /> },
      { path: "products/:id", element: <AdminWorkspacePage moduleId="product-detail" /> },
      { path: "suppliers/:id", element: <AdminWorkspacePage moduleId="supplier-detail" /> },
      { path: "purchasing/:id", element: <AdminWorkspacePage moduleId="po-detail" /> },
      { path: "customers/:id", element: <AdminWorkspacePage moduleId="customer-detail" /> }
    ]
  },
  { path: "*", element: <NotFound /> }
]);
